import { useAuth0 } from "@auth0/auth0-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGlobalLoadingWhile } from "../components/loading/global-loading-context";
import { OnboardingWizard } from "../components/onboarding/OnboardingWizard";
import { reconstructCheckedStepIds } from "../components/onboarding/onboardingSave";
import { useAnalytics } from "../lib/analytics";
import { userQueryOptions } from "../lib/userQueries";
import type { UserDetails } from "../services/userService";
import type { OnboardingAnswers, UserData } from "../types/onboarding";
import styles from "./Onboarding.module.css";

export function Onboarding() {
	const navigate = useNavigate();
	const { logEvent } = useAnalytics();
	const { user } = useAuth0();
	const queryClient = useQueryClient();
	const [userData, setUserData] = useState<UserDetails | null>(null);
	const [initialStepIds, setInitialStepIds] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const hasInitialized = useRef(false);

	// Drive the single, centered GlobalLoadingModal during the initial user-data
	// fetch instead of rendering a second (top-aligned) loader on this page —
	// otherwise both show at once after 2FA → onboarding for a new user.
	useGlobalLoadingWhile(isLoading);

	useEffect(() => {
		// Wait for Auth0 to populate the user before initializing — otherwise we'd
		// cache the profile under the non-user-scoped ['user', undefined] key and
		// never re-run (the effect re-fires when `user` changes).
		if (hasInitialized.current || !user?.sub) return;

		hasInitialized.current = true;
		const sub = user.sub;

		const loadUserData = async () => {
			try {
				// Shared cache: dedupes with the app-wide /api/user query instead of
				// firing its own request on the onboarding screen.
				const profile = await queryClient.ensureQueryData(
					userQueryOptions(sub),
				);
				const data = profile.details ?? null;
				setUserData(data);
				// Step progress lives in UserStepState (not /api/user); reconstruct the
				// checked step ids so resume knows the steps screen was completed.
				if (data) {
					setInitialStepIds(
						await reconstructCheckedStepIds(
							data.fertilityStatus,
							data.fertilityRole,
							data.fertilityJourneyTypes ?? [],
						),
					);
				}
				logEvent("onboarding_page_loaded", undefined, {
					user_type: data?.userType || "unknown",
					is_onboarded: String(data?.isOnboarded),
				});
			} catch (error) {
				console.error("Onboarding - error loading user:", error);
				logEvent("onboarding_page_load_error", undefined, {
					error_message: error instanceof Error ? error.message : "unknown",
				});
			} finally {
				setIsLoading(false);
			}
		};

		loadUserData();
	}, [logEvent, queryClient, user]);

	const handleComplete = async (answers: OnboardingAnswers) => {
		// Navigate to thank you page - completion API will be called there
		logEvent("onboarding_wizard_completed", undefined, {
			total_answers: Object.keys(answers).length.toString(),
		});

		navigate("/onboarding/thank-you", { replace: true });
	};

	// While loading, render nothing — the centered GlobalLoadingModal (driven by
	// the effect above) is the only loader shown.
	if (isLoading) {
		return null;
	}

	return (
		<div className={styles.page}>
			<OnboardingWizard
				userData={userData as UserData | null}
				onComplete={handleComplete}
				initialStepIds={initialStepIds}
			/>
		</div>
	);
}
